import { create } from "zustand";

// Video categories based on your webm files
export type BottleType = 'Heart' | 'Normal' | 'Weird';
export type BottleColor = 'Black' | 'Blue' | 'Green' | 'Pink' | 'Rainbow' | 'Yellow';
export type EffectType = 'Cauldron' | 'CauldronSmoke' | 'ScreenSmoke';

// Video trigger types for different game events
export type VideoTrigger = 
  | { type: 'potion'; bottleType: BottleType; color: BottleColor }
  | { type: 'effect'; effectType: EffectType }
  | { type: 'custom'; videoPath: string }
  | { type: 'looping'; videoPath: string }
  | { type: 'looped'; bottleType: BottleType; color: BottleColor; loopLastSeconds: number; loopDuration: number };

// Video configuration mapping
const VIDEO_PATHS: Record<string, string> = {
  // Potion bottles
  'potion_Heart_Black': '/webm/MB_HeartBottle_FINAL_Black_1.webm',
  'potion_Heart_Blue': '/webm/MB_HeartBottle_FINAL_Blue_1.webm',
  'potion_Heart_Green': '/webm/MB_HeartBottle_FINAL_Green_1.webm',
  'potion_Heart_Pink': '/webm/MB_HeartBottle_FINAL_Pink.webm',
  'potion_Heart_Rainbow': '/webm/MB_HeartBottle_FINAL_Rainbow_1.webm',
  'potion_Heart_Yellow': '/webm/MB_HeartBottle_FINAL_Yellow_1.webm',
  
  'potion_Normal_Black': '/webm/MB_NormalBottle_Final_Black.webm',
  'potion_Normal_Blue': '/webm/MB_NormalBottle_Final_Blue.webm',
  'potion_Normal_Green': '/webm/MB_NormalBottle_Final_Green.webm',
  'potion_Normal_Pink': '/webm/MB_NormalBottle_Final_Pink.webm',
  'potion_Normal_Rainbow': '/webm/MB_NormalBottle_Final_Rainbow.webm',
  'potion_Normal_Yellow': '/webm/MB_NormalBottle_Final_Yellow.webm',
  
  'potion_Weird_Black': '/webm/MB_WierdBottle_Final_Black_1.webm',
  'potion_Weird_Blue': '/webm/MB_WierdBottle_Final_Blue_1.webm',
  'potion_Weird_Green': '/webm/MB_WierdBottle_Final_Green_1.webm',
  'potion_Weird_Pink': '/webm/MB_WierdBottle_Final_Pink_1.webm',
  'potion_Weird_Rainbow': '/webm/MB_WierdBottle_Final_Rainbow_1.webm',
  'potion_Weird_Yellow': '/webm/MB_WierdBottle_Final_Yellow.webm',
  
  // Effects
  'effect_Cauldron': '/webm/Cauldron_1.webm',
  'effect_CauldronSmoke': '/webm/Cauldron_smokeeffect.webm',
  'effect_ScreenSmoke': '/webm/Screen_Smoke.webm',
};

// Get all video paths for preloading
export const getAllVideoPaths = (): string[] => {
  return Object.values(VIDEO_PATHS);
};

// Generate video key from trigger
const getVideoKey = (trigger: VideoTrigger): string => {
  switch (trigger.type) {
    case 'potion':
      return `potion_${trigger.bottleType}_${trigger.color}`;
    case 'effect':
      return `effect_${trigger.effectType}`;
    case 'custom':
      return trigger.videoPath;
    case 'looping':
      return trigger.videoPath;
    default:
      throw new Error('Invalid video trigger type');
  }
};

// Get video path from trigger
export const getVideoPath = (trigger: VideoTrigger): string => {
  if (trigger.type === 'custom') {
    return trigger.videoPath;
  }
  
  if (trigger.type === 'looping') {
    return trigger.videoPath;
  }
  
  if (trigger.type === 'looped') {
    // For looped videos, use the same path as regular potion videos
    const key = `potion_${trigger.bottleType}_${trigger.color}`;
    const path = VIDEO_PATHS[key];
    if (!path) {
      console.warn(`Video not found for looped trigger:`, trigger);
      return '';
    }
    return path;
  }
  
  const key = getVideoKey(trigger);
  const path = VIDEO_PATHS[key];
  
  if (!path) {
    console.warn(`Video not found for trigger:`, trigger);
    return '';
  }
  
  return path;
};

// Video manager state
interface VideoManagerState {
  currentVideo: string | null;
  currentTrigger: VideoTrigger | null;
  isPlaying: boolean;
  isLoading: boolean;
  isLooping: boolean;
  preloadedVideos: Set<string>;
  videoQueue: VideoTrigger[];
  playHistory: Array<{
    trigger: VideoTrigger;
    videoPath: string;
    timestamp: Date;
    potionName?: string;
  }>;
}

interface VideoManagerActions {
  playVideo: (trigger: VideoTrigger) => void;
  playPotionVideo: (trigger: VideoTrigger, potionName?: string) => void;
  queueVideo: (trigger: VideoTrigger) => void;
  stopVideo: () => void;
  clearQueue: () => void;
  onVideoEnded: () => void;
  onVideoLoaded: (videoPath: string) => void;
  preloadVideo: (videoPath: string) => void;
  preloadAllVideos: () => void;
  setLooping: (isLooping: boolean) => void;
  getPlayHistory: () => Array<{
    trigger: VideoTrigger;
    videoPath: string;
    timestamp: Date;
    potionName?: string;
  }>;
}

export type VideoManagerStore = VideoManagerState & VideoManagerActions;

const initialState: VideoManagerState = {
  currentVideo: null,
  currentTrigger: null,
  isPlaying: false,
  isLoading: false,
  isLooping: false,
  preloadedVideos: new Set(),
  videoQueue: [],
  playHistory: [],
};

export const useVideoManager = create<VideoManagerStore>()((set, get) => ({
  ...initialState,

  playVideo: (trigger: VideoTrigger) => {
    const videoPath = getVideoPath(trigger);
    if (!videoPath) {
      console.warn('Cannot play video: invalid path for trigger', trigger);
      return;
    }

    // If a video is currently playing, queue this one
    if (get().isPlaying) {
      get().queueVideo(trigger);
      return;
    }

    // Add to play history
    const playHistory = get().playHistory;
    playHistory.push({
      trigger,
      videoPath,
      timestamp: new Date()
    });

    console.log('Playing video:', videoPath, trigger);
    set({ 
      currentVideo: videoPath,
      currentTrigger: trigger,
      isPlaying: true, 
      isLoading: true,
      isLooping: false,
      playHistory: [...playHistory]
    });
  },

  playPotionVideo: (trigger: VideoTrigger, potionName?: string) => {
    const videoPath = getVideoPath(trigger);
    if (!videoPath) {
      console.warn('Cannot play potion video: invalid path for trigger', trigger);
      return;
    }

    // If a video is currently playing, queue this one
    if (get().isPlaying) {
      get().queueVideo(trigger);
      return;
    }

    // Add to play history with potion name
    const playHistory = get().playHistory;
    playHistory.push({
      trigger,
      videoPath,
      timestamp: new Date(),
      potionName
    });

    console.log('Playing potion video:', videoPath, trigger, potionName ? `for ${potionName}` : '');
    set({ 
      currentVideo: videoPath,
      currentTrigger: trigger,
      isPlaying: true, 
      isLoading: true,
      isLooping: false,
      playHistory: [...playHistory]
    });
  },

  queueVideo: (trigger: VideoTrigger) => {
    const videoQueue = get().videoQueue;
    set({ videoQueue: [...videoQueue, trigger] });
  },

  stopVideo: () => {
    set({ 
      currentVideo: null,
      currentTrigger: null,
      isPlaying: false, 
      isLoading: false,
      isLooping: false
    });
    
    // Play next video in queue if any
    const { videoQueue } = get();
    if (videoQueue.length > 0) {
      const nextTrigger = videoQueue[0];
      const remainingQueue = videoQueue.slice(1);
      set({ videoQueue: remainingQueue });
      
      // Small delay to ensure clean transition
      setTimeout(() => {
        get().playVideo(nextTrigger);
      }, 100);
    }
  },

  clearQueue: () => {
    set({ videoQueue: [] });
  },

  onVideoEnded: () => {
    console.log('Video ended');
    get().stopVideo();
  },

  onVideoLoaded: (videoPath: string) => {
    const preloadedVideos = get().preloadedVideos;
    preloadedVideos.add(videoPath);
    set({ 
      preloadedVideos: new Set(preloadedVideos),
      isLoading: false 
    });
    console.log('Video loaded:', videoPath);
  },

  preloadVideo: (videoPath: string) => {
    const { preloadedVideos } = get();
    if (preloadedVideos.has(videoPath)) {
      return; // Already preloaded
    }

    // Create a hidden video element for preloading
    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true; // Required for autoplay policies
    video.style.display = 'none';
    
    video.addEventListener('loadeddata', () => {
      get().onVideoLoaded(videoPath);
      document.body.removeChild(video);
    });
    
    video.addEventListener('error', (e) => {
      console.error('Failed to preload video:', videoPath, e);
      document.body.removeChild(video);
    });
    
    video.src = videoPath;
    document.body.appendChild(video);
  },

  preloadAllVideos: () => {
    console.log('Preloading all videos...');
    const allPaths = getAllVideoPaths();
    allPaths.forEach(path => {
      get().preloadVideo(path);
    });
  },

  setLooping: (isLooping: boolean) => {
    set({ isLooping });
  },

  getPlayHistory: () => {
    return get().playHistory;
  },
}));
